/*
 * Asset Library's Brand Templates tab — a grid of the org's saved brand
 * templates. My Templates (local uploads) never appears here — that stays in
 * Apply Brand's own Saved Templates tab.
 *
 * Clicking a card opens its detail view; there's no separate select step.
 * Instead of rendering its own button, this tab reports its footer state up
 * to AssetLibraryContent (see asset-library/types.ts) so there's exactly ONE
 * action button, not a duplicate inline + footer pair. That action routes
 * into Apply Brand's Saved Templates tab with the template pre-selected —
 * this tab never extracts or applies anything itself.
 *
 * Two data sources, mirroring SlideTemplatesPanel's `templatesOptions` split:
 * in-pane (no `dialogTemplates`) fetches directly since this pane has auth;
 * the Office Dialog window (`dialogTemplates` supplied) mirrors the load
 * state useDialogBrandTemplates.ts owns, since the dialog has no bearer token.
 */

import { useEffect, useRef, useState } from "react";
import { useSavedBrandTemplates } from "../../apply-brand/hooks/useSavedBrandTemplates";
import { setPendingSavedTemplateSelection } from "../../apply-brand/services/pendingSavedTemplateSelection";
import type { SavedBrandTemplate } from "../../apply-brand/services/brandTemplatesApi";
import { requestDeckorateView } from "../../../../../../integrations/tools/deckorate";
import type { DialogBrandTemplates } from "../hooks/useDialogBrandTemplates";
import type { AssetLibraryFooterState } from "../types";
import { consumePendingBrandTemplatePreview } from "../services/brandTemplatePreviewHandoff";
import styles from "./AssetLibraryDialog.module.css";

interface BrandTemplatesTabProps {
  readonly onClose: () => void;
  /** Supplied only by AssetLibraryDialogApp (the real Office Dialog window). */
  readonly dialogTemplates?: DialogBrandTemplates;
  /** Reports the current footer label/button up to AssetLibraryContent —
   *  null while browsing the grid (nothing open to act on yet). */
  readonly onFooterStateChange: (state: AssetLibraryFooterState | null) => void;
}

export function BrandTemplatesTab({ onClose, dialogTemplates, onFooterStateChange }: Readonly<BrandTemplatesTabProps>) {
  if (dialogTemplates) return <DialogBrandTemplatesView dialogTemplates={dialogTemplates} onFooterStateChange={onFooterStateChange} />;
  return <InlineBrandTemplatesView onClose={onClose} onFooterStateChange={onFooterStateChange} />;
}

/** In-pane path: fetches directly (this pane has auth) and performs the
 *  hand-off + navigation itself, then closes the (inline-only) overlay. */
function InlineBrandTemplatesView({
  onClose, onFooterStateChange,
}: Readonly<{ onClose: () => void; onFooterStateChange: (state: AssetLibraryFooterState | null) => void }>) {
  const { loadState, templates, retry } = useSavedBrandTemplates();

  const handleSwitch = (id: string) => {
    const template = templates.find((candidate) => candidate.id === id);
    if (!template) return;
    setPendingSavedTemplateSelection({ kind: "org", template });
    requestDeckorateView("apply-brand");
    onClose();
  };

  return (
    <BrandTemplatesGridOrDetail
      status={loadState.status}
      errorMessage={loadState.status === "error" ? loadState.message : undefined}
      templates={templates}
      onRetry={retry}
      onSwitch={handleSwitch}
      onFooterStateChange={onFooterStateChange}
    />
  );
}

/** Office Dialog path: mirrors the load state owned by useDialogBrandTemplates
 *  and delegates the switch action to it — that hook posts the pick back to
 *  the pane, which does the actual hand-off/navigate/close. */
function DialogBrandTemplatesView({
  dialogTemplates, onFooterStateChange,
}: Readonly<{ dialogTemplates: DialogBrandTemplates; onFooterStateChange: (state: AssetLibraryFooterState | null) => void }>) {
  const { state, request, retry, switchTo } = dialogTemplates;
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    request();
    // Runs once, when the tab first mounts — the load itself is lazy so
    // opening the Asset Library for slides never triggers an unused fetch.
  }, [request]);

  const status = state.status === "idle" ? "loading" : state.status;
  return (
    <BrandTemplatesGridOrDetail
      status={status}
      errorMessage={state.status === "error" ? state.message : undefined}
      templates={state.status === "ready" ? state.org : []}
      onRetry={retry}
      onSwitch={switchTo}
      onFooterStateChange={onFooterStateChange}
    />
  );
}

/** Fallback when a template has neither theme dark colours nor swatches —
 *  matches the neutral fallback Review & Customise's colour fields use. */
const HEADER_FALLBACK_COLOR = "334155";

function headerColorFor(template: SavedBrandTemplate): string {
  return template.themeColors?.dk2 ?? template.themeColors?.dk1 ?? template.colors?.[0] ?? HEADER_FALLBACK_COLOR;
}

function BrandTemplatesGridOrDetail({
  status, errorMessage, templates, onRetry, onSwitch, onFooterStateChange,
}: Readonly<{
  status: "loading" | "error" | "ready";
  errorMessage: string | undefined;
  templates: readonly SavedBrandTemplate[];
  onRetry: () => void;
  onSwitch: (id: string) => void;
  onFooterStateChange: (state: AssetLibraryFooterState | null) => void;
}>) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = status === "ready" && openId ? templates.find((t) => t.id === openId) ?? null : null;
  const appliedPendingPreviewRef = useRef(false);

  // Apply Brand's own Saved Templates tab can route straight in here for a
  // specific template (its eye icon — see brandTemplatePreviewHandoff.ts).
  // Applies once, as soon as the real list is ready, so this open lands on
  // that template's detail instead of the grid.
  useEffect(() => {
    if (appliedPendingPreviewRef.current || status !== "ready") return;
    appliedPendingPreviewRef.current = true;
    const pendingId = consumePendingBrandTemplatePreview();
    if (pendingId && templates.some((t) => t.id === pendingId)) setOpenId(pendingId);
  }, [status, templates]);

  // Reports this tab's own footer label (never the generic default); clears
  // it on unmount so a stale button doesn't linger on another tab.
  useEffect(() => {
    onFooterStateChange(
      open
        ? { selectionLabel: open.name, actionLabel: "Use this template", actionDisabled: false, onAction: () => onSwitch(open.id) }
        : { selectionLabel: "0 selected", actionLabel: "Open in Apply Brand", actionDisabled: true, onAction: () => undefined },
    );
    return () => onFooterStateChange(null);
  }, [open, onSwitch, onFooterStateChange]);

  if (status === "loading") {
    return (
      <div className={styles.centered}>
        <span className={styles.spinner} aria-hidden="true" />
        <span>Loading brand templates…</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>{errorMessage ?? "Couldn't load brand templates."}</span>
        <button type="button" className={styles.retryBtn} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (open) {
    return <BrandTemplateDetail template={open} onBack={() => setOpenId(null)} />;
  }

  if (templates.length === 0) {
    return <div className={styles.comingSoon}><span className={styles.comingSoonText}>No brand templates yet.</span></div>;
  }

  return (
    <div className={styles.brandTemplatesScroll}>
      <p className={styles.brandTemplatesIntro}>
        Your organisation&rsquo;s <b>brand templates</b>. Preview one, then use it to load its colours, fonts and styles into Apply Brand.
      </p>
      <div className={styles.savedTemplatesHeader}>
        <span className={styles.savedTemplatesTitle}>BRAND TEMPLATES</span>
      </div>
      <ul className={styles.brandTemplatesGrid}>
        {templates.map((template) => (
          <li key={template.id}>
            <button type="button" className={styles.brandTemplateCard} onClick={() => setOpenId(template.id)}>
              <span className={styles.brandTemplateCardHeader} style={{ backgroundColor: `#${headerColorFor(template)}` }}>
                Aa
              </span>
              <span className={styles.brandTemplateCardSwatches}>
                {(template.colors ?? []).slice(0, 6).map((hex) => (
                  <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} />
                ))}
              </span>
              <span className={styles.brandTemplateContentBars} aria-hidden="true">
                <span className={styles.brandTemplateContentBarWide} />
                <span className={styles.brandTemplateContentBarNarrow} />
              </span>
              <span className={styles.brandTemplateCardFooter}>
                <span className={styles.brandTemplateCardName} title={template.name}>{template.name}</span>
                {template.brand && <span className={styles.brandTemplateCardBrand}>{template.brand.toUpperCase()}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The "Use this template" action lives in the shared footer (see
 *  BrandTemplatesGridOrDetail's onFooterStateChange effect above) — not
 *  rendered here, so there's exactly one button, not a duplicate pair. */
function BrandTemplateDetail({ template, onBack }: Readonly<{
  template: SavedBrandTemplate;
  onBack: () => void;
}>) {
  return (
    <div className={styles.brandTemplateDetail}>
      <div className={styles.brandTemplateDetailHeaderRow}>
        <button type="button" className={styles.brandTemplateBackBtn} onClick={onBack}>
          &lsaquo; All brand templates
        </button>
        <span className={styles.brandTemplateDetailTitle}>{template.name}</span>
      </div>

      <div className={styles.brandTemplateDetailCard}>
        <div className={styles.brandTemplateDetailPreviewHeader} style={{ backgroundColor: `#${headerColorFor(template)}` }}>
          {template.name}
        </div>
        <div className={styles.brandTemplateDetailPreviewBody}>
          <span className={styles.brandTemplateContentBars} aria-hidden="true">
            <span className={styles.brandTemplateContentBarWide} />
            <span className={styles.brandTemplateContentBarNarrow} />
          </span>
          <span className={styles.brandTemplateCardSwatches}>
            {(template.colors ?? []).map((hex) => (
              <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
            ))}
          </span>
        </div>
      </div>

      {(template.brand || template.aspectRatio) && (
        <div className={styles.brandTemplateDetailMeta}>
          {template.brand && (
            <span className={styles.brandTemplateDetailMetaItem}>
              <span className={styles.brandTemplateDetailMetaLabel}>Brand</span>
              <span>{template.brand}</span>
            </span>
          )}
          {template.aspectRatio && (
            <span className={styles.brandTemplateDetailMetaItem}>
              <span className={styles.brandTemplateDetailMetaLabel}>Format</span>
              <span>{template.aspectRatio}</span>
            </span>
          )}
        </div>
      )}

      <p className={styles.brandTemplatesIntro}>Loads the brand into Apply Brand — you review &amp; apply from there.</p>
    </div>
  );
}
